import * as Headless from '@headlessui/react';
import throttle from 'lodash/throttle';
import React, { forwardRef, useCallback, useMemo } from 'react';
import Link from '@/components/Link';
import cn from '@/utils/cn';
import TouchTarget from './TouchTarget';

const styles = {
  base: [
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-sm/6 font-semibold',
    'px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)]',
    'focus:outline-none data-focus:outline-solid data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',
    'data-disabled:opacity-50',
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-1 *:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-(--btn-icon) forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
  ],
  solid: [
    'border-transparent bg-(--btn-bg)',
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',
    'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)',
  ],
  outline: [
    'border-primary-green text-primary-green data-active:bg-primary-green data-active:text-white data-hover:bg-primary-green data-hover:text-white',
    '[--btn-icon:var(--primary-green)] data-active:[--btn-icon:var(--grayscale-white)] data-hover:[--btn-icon:var(--grayscale-white)]',
  ],
  outlineDanger: [
    'border-secondary-red text-secondary-red data-active:bg-secondary-red data-active:text-white data-hover:bg-secondary-red data-hover:text-white',
    '[--btn-icon:var(--primary-red)] data-active:[--btn-icon:var(--grayscale-white)] data-hover:[--btn-icon:var(--grayscale-white)]',
  ],
  plain: [
    'border-transparent text-grayscale-darkGray data-active:bg-grayscale-lightestGray data-hover:text-primary-greenHover',
    '[--btn-icon:var(--grayscale-lightGray)] data-active:[--btn-icon:var(--grayscale-darkGray)] data-hover:[--btn-icon:var(--grayscale-darkGray)]',
  ],
  plainLink: [
    'border-transparent text-primary-green data-active:bg-grayscale-lightestGray data-hover:bg-grayscale-lightestGray data-hover:text-primary-greenHover',
    '[--btn-icon:var(--primary-green)] data-active:[--btn-icon:var(--primary-green)] data-hover:[--btn-icon:var(--primary-green-hover)]',
  ],
  colors: {
    primary: [
      'text-white [--btn-bg:var(--primary-green)] [--btn-border:var(--primary-green-hover)] [--btn-hover-overlay:color-mix(in_srgb,var(--grayscale-white)_10%,transparent)]',
      '[--btn-icon:color-mix(in_srgb,var(--grayscale-white)_60%,transparent)] data-active:[--btn-icon:color-mix(in_srgb,var(--grayscale-white)_80%,transparent)] data-hover:[--btn-icon:color-mix(in_srgb,var(--grayscale-white)_80%,transparent)]',
    ],
    danger: [
      'text-white [--btn-hover-overlay:color-mix(in_srgb,var(--grayscale-white)_10%,transparent)] [--btn-bg:var(--primary-red)] [--btn-border:var(--secondary-red-hover)]',
      '[--btn-icon:color-mix(in_srgb,var(--grayscale-white)_60%,transparent)] data-active:[--btn-icon:color-mix(in_srgb,var(--grayscale-white)_80%,transparent)] data-hover:[--btn-icon:color-mix(in_srgb,var(--grayscale-white)_80%,transparent)]',
    ],
    gray: [
      'text-grayscale-darkestGray [--btn-bg:var(--grayscale-lightGray)] [--btn-border:var(--grayscale-lightGray)] [--btn-hover-overlay:color-mix(in_srgb,var(--grayscale-darkGray)_10%,transparent)] data-active:[--btn-border:var(--grayscale-lightGray)] data-hover:[--btn-border:var(--grayscale-lightGray)]',
      '[--btn-icon:var(--grayscale-darkGray)] data-active:[--btn-icon:var(--grayscale-darkGray)] data-hover:[--btn-icon:var(--grayscale-darkGray)]',
    ],
  },
};

export type TButtonProps = (
  | { color?: 'primary' | 'danger' | 'gray'; outline?: true; plain?: never }
  | { color?: never; outline?: never; plain: true }
) & {
  children: React.ReactNode;
  className?: string;
  clickRateLimitingDelayMilliseconds?: number;
  dataQaId?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
} & (
    | Omit<Headless.ButtonProps, 'as' | 'className' | 'disabled'>
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
  );

const Button = forwardRef<HTMLElement, TButtonProps>(function Button(
  {
    children,
    className,
    clickRateLimitingDelayMilliseconds = 1000,
    color,
    dataQaId,
    disabled,
    onClick,
    outline,
    plain,
    ...props
  },
  ref
) {
  const isLink = 'href' in props;

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        return;
      }
      if (onClick) {
        onClick(event);
      }
    },
    [disabled, onClick]
  );

  const throttledHandleClick = useMemo(
    () =>
      throttle(handleButtonClick, clickRateLimitingDelayMilliseconds, {
        leading: true,
        trailing: false,
      }),
    [handleButtonClick, clickRateLimitingDelayMilliseconds]
  );

  const getVariantStyles = () => {
    if (outline)
      return color === 'danger' ? styles.outlineDanger : styles.outline;
    if (isLink) return styles.plainLink;
    if (plain) return styles.plain;
    return cn(styles.solid, styles.colors[color ?? 'primary']);
  };

  const classes = cn(styles.base, getVariantStyles(), className);

  return 'href' in props ? (
    <Link
      {...props}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      dataQaId={dataQaId}
    >
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button
      // data-qa-id before {...props} allows explicit override
      data-qa-id={dataQaId}
      {...props}
      className={cn(classes)}
      disabled={disabled}
      onClick={throttledHandleClick}
      ref={ref}
    >
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  );
});

export default Button;
